// src/components/FileBrowser.js
import React, { useState, useCallback, useRef, useContext, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';
import FileBrowserToolbar from './FileBrowserToolbar';
import FileGridView from './FileGridView';
import FileListView from './FileListView';
import FileDetailsModal from './FileDetailsModal';
import UploadFileModal from './UploadFileModal';
import ImportWizardModal from './ImportWizardModal';
import AlgoliaSearchPanel from './search/AlgoliaSearchPanel';
import ReverseSearchModal from './search/ReverseSearchModal';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';
import * as FileService from '../services/FileService.mjs';
import { filesFromDataTransfer } from '../utils/folderUpload';
import { reportError, notify } from '../notifications/errorBus';
import { useConfirm } from '../context/ConfirmContext';
import { WorkspaceContext } from '../context/WorkspaceContext';
import usePersistedState from '../hooks/usePersistedState';
import { DEFAULT_DENSITY, densityOf, densityVars, isDensity } from '../config/gridDensity';
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS } from '../config/fileColumns';
import { DEFAULT_SORT, isSort } from '../config/fileSorts';
import SelectionBar from './file/SelectionBar';
import FilterRail, { RAIL_DEFAULT, isRailWidth } from './file/FilterRail';
import { QUERY_PARAM } from './shell/GlobalSearch';
import "../css/FileBrowser.css";
import "../css/ReverseSearch.css";

// A flat drop at/above this many files goes to the Import wizard (filtering,
// bulk tagging, progress/retry, resume); fewer stay in the quick-upload modal.
const BULK_DROP_THRESHOLD = 25;

const VIEW_MODES = ['grid', 'table'];

const FileBrowser = () => {
    // `/?project=<id>` arrives here from the Projects page. Read once, at mount:
    // the panel owns its filters from then on, and re-seeding on every render
    // would fight the user the moment they cleared it.
    const location = useLocation();
    const initialProjectId = useMemo(
        () => new URLSearchParams(location.search).get('project') || null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // The search text lives in the URL (`/?q=…`), which is what lets the app
    // shell's one search box drive this page from anywhere — and makes a search
    // something you can link to.
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get(QUERY_PARAM) ?? '';
    const setQuery = useCallback((next) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (next) params.set(QUERY_PARAM, next); else params.delete(QUERY_PARAM);
            return params;
        }, { replace: true });
    }, [setSearchParams]);

    // ── State ───────────────────────────────────────────────────────────────
    const [files,                setFiles]                = useState([]);
    // Grid-vs-table and the chosen columns are per-user working preferences, not
    // navigation state — resetting them on every reload made the table view feel
    // like it kept forgetting itself.
    const [viewMode,             setViewMode]             = usePersistedState(
        'qdex-file-view-mode', 'grid', (v) => VIEW_MODES.includes(v),
    );
    // Ordering and card size are working preferences, like the view mode: a
    // person who browses newest-first, in compact cards, means it tomorrow too.
    const [sort,                 setSort]                 = usePersistedState('qdex-file-sort', DEFAULT_SORT, isSort);
    const [gridDensity,          setGridDensity]          = usePersistedState('qdex-grid-density', DEFAULT_DENSITY, isDensity);
    // How much room the filters get, and whether they're on screen at all.
    const [railWidth,            setRailWidth]            = usePersistedState('qdex-filter-rail-width', RAIL_DEFAULT, isRailWidth);
    const [filtersCollapsed,     setFiltersCollapsed]     = usePersistedState('qdex-filters-collapsed', false, (v) => typeof v === 'boolean');
    const [selection,            setSelection]            = useState([]);
    const [lastSelectedId,       setLastSelectedId]       = useState(null);
    const [selectedFileForModal, setSelectedFileForModal] = useState(null);
    const [deleting,             setDeleting]             = useState(false);
    const [searchStats,          setSearchStats]          = useState({ nbHits: 0, loading: false, appliedSort: DEFAULT_SORT });

    const gridVars = densityVars(densityOf(gridDensity));

    // ── Whole-browser drag-and-drop ──────────────────────────────────────────
    // Dropping files anywhere on the browser opens the upload modal pre-seeded
    // with the dropped FileList. A counter ref absorbs the dragenter/dragleave
    // flicker that fires as the pointer crosses child elements, so the overlay
    // only hides once the drag truly leaves the container.
    const [isDragging,    setIsDragging]    = useState(false);
    const [droppedFiles,  setDroppedFiles]  = useState(null);
    const [importEntries, setImportEntries] = useState(null);
    // The file the user dropped onto "Find matches" — not an upload, and never
    // added to the library. See ReverseSearchModal.
    const [searchFile,    setSearchFile]    = useState(null);
    const [hoveredTarget, setHoveredTarget] = useState(null);
    const dragDepthRef = useRef(0);

    // Dropping a FOLDER goes to the bulk Import wizard (which applies tags), so
    // gate it on the same role the toolbar's Import button uses.
    const { activeWorkspaceRole } = useContext(WorkspaceContext);
    const confirm = useConfirm();
    const canBulkImport = ["editor", "admin", "owner"].includes(activeWorkspaceRole);

    const dragHasFiles = (e) =>
        Array.from(e.dataTransfer?.types || []).includes('Files');

    const handleDragOver = (e) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragEnter = (e) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        dragDepthRef.current += 1;
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragging(false);
    };

    /**
     * Dropped onto "Find matches": search the library BY this file's content.
     *
     * Only the first file is used. "Find the assets matching these six things"
     * is a different feature (a batch dedupe pass) with a different result
     * shape, and quietly doing it for one of the six would be worse than being
     * explicit.
     */
    const handleSearchDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragging(false);
        setHoveredTarget(null);

        const file = e.dataTransfer?.files?.[0];
        if (!file) return;
        if (e.dataTransfer.files.length > 1) {
            notify({
                type: 'info',
                title: 'Searching with the first file',
                message: `Content search takes one file at a time — using "${file.name}".`,
            });
        }
        setSearchFile(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        dragDepthRef.current = 0;
        setIsDragging(false);
        setHoveredTarget(null);

        const dt = e.dataTransfer;
        if (!dt) return;

        // Detect a dropped folder (must read webkitGetAsEntry synchronously, while
        // the drag data store is still valid). A folder → the bulk Import wizard;
        // loose files → the quick-upload modal (its hash check can't read a
        // directory, which is what produced the "operation was aborted" error).
        const entries = dt.items
            ? Array.from(dt.items)
                  .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
                  .filter(Boolean)
            : [];
        const hasDirectory = entries.some((en) => en && en.isDirectory);

        if (hasDirectory) {
            if (!canBulkImport) {
                notify({
                    type: "warning",
                    title: "Folder import needs the Editor role",
                    message: "You can upload individual files, but importing a whole folder requires Editor access.",
                });
                return;
            }
            // Enumerate now, while the entries are valid, then hand the resulting
            // {file, relativePath} list to the wizard.
            let dropped = [];
            try {
                dropped = await filesFromDataTransfer(dt);
            } catch (err) {
                reportError(err, { title: "Could not read the dropped folder" });
                return;
            }
            if (dropped.length > 0) {
                setImportEntries(dropped);
            } else {
                notify({
                    type: "warning",
                    title: "Nothing to import",
                    message: "That folder had no importable files.",
                });
            }
            return;
        }

        const files = dt.files;
        if (!files || files.length === 0) return;
        // Loose files: a large batch is better served by the wizard; a quick
        // handful stays in the fast modal. (Folders already routed above.)
        if (files.length >= BULK_DROP_THRESHOLD && canBulkImport) {
            setImportEntries(Array.from(files).map((file) => ({ file, relativePath: file.name })));
        } else {
            setDroppedFiles(files);
        }
    };

    const [visibleColumns, setVisibleColumns] = usePersistedState(
        'qdex-file-columns',
        DEFAULT_COLUMNS,
        (v) => Array.isArray(v) && v.every((c) => AVAILABLE_COLUMNS.includes(c)),
    );

    // ── Results refresh (M-F4) ───────────────────────────────────────────────
    // The Algolia panel registers its refetch here so mutations (upload,
    // delete, metadata edit) can refresh the visible result list — previously
    // onFilesChanged reloaded file *groups*, leaving the grid stale.
    const algoliaRefetchRef = useRef(null);
    const registerAlgoliaRefetch = useCallback((fn) => { algoliaRefetchRef.current = fn; }, []);
    const handleFilesChanged = useCallback(() => { algoliaRefetchRef.current?.(); }, []);

    // The global upload manager runs uploads outside this component (even after
    // the upload modal closes / the user navigates away), so it announces landed
    // files via a window event rather than a prop callback. Refresh on it.
    useEffect(() => {
        const onChanged = () => handleFilesChanged();
        window.addEventListener('qdex:files-changed', onChanged);
        return () => window.removeEventListener('qdex:files-changed', onChanged);
    }, [handleFilesChanged]);

    // ── Callbacks for AlgoliaSearchPanel ────────────────────────────────────
    // Results refresh for all sorts of reasons the user didn't ask for — a
    // background upload landing fires `qdex:files-changed`, which refetches.
    // Clearing the selection outright meant an unrelated tray upload could wipe
    // a 40-file ctrl-click selection mid-workflow. Intersect instead: ids that
    // genuinely left the result set drop out, everything else survives.
    const handleAlgoliaResults = useCallback((hits) => {
        setFiles(hits);
        const ids = new Set(hits.map((h) => h.id));
        setSelection((prev) => prev.filter((id) => ids.has(id)));
        setLastSelectedId((prev) => (prev && ids.has(prev) ? prev : null));
    }, []);

    const handleAlgoliaStats = useCallback((stats) => {
        setSearchStats(stats);
    }, []);

    // ── Selection logic (M-F5) ──────────────────────────────────────────────
    const handleFileClick = (fileId, event) => {
        if (event.ctrlKey || event.metaKey) {
            toggleSelection(fileId);
        } else if (event.shiftKey) {
            event.preventDefault(); // prevent browser text selection
            handleRangeSelection(fileId);
        } else {
            setLastSelectedId(fileId);
            const file = files.find(f => f.id === fileId);
            if (file) setSelectedFileForModal(file);
        }
    };

    const handleCheckboxClick = (fileId) => toggleSelection(fileId);

    // Right-clicking an unselected card used to ADD it to the current selection,
    // so a context-menu action silently applied to the previously-selected files
    // too. Every file manager replaces the selection here instead.
    const handleContextSelect = useCallback((fileId) => {
        setSelection([fileId]);
        setLastSelectedId(fileId);
    }, []);

    const toggleSelection = (id) => {
        setSelection(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
        // M-F5: anchor updates live OUTSIDE the state updater (no side effects
        // in updaters — they double-invoke in StrictMode). The last item the
        // user touched is the anchor, whether it was selected or deselected.
        setLastSelectedId(id);
    };

    const handleRangeSelection = (currentId) => {
        if (!lastSelectedId || files.length === 0) { toggleSelection(currentId); return; }
        const ci = files.findIndex(f => f.id === currentId);
        const li = files.findIndex(f => f.id === lastSelectedId);
        if (ci === -1 || li === -1) { toggleSelection(currentId); return; }
        const rangeIds = files.slice(Math.min(ci, li), Math.max(ci, li) + 1).map(f => f.id);
        // M-F5: shift-click REPLACES the selection with the anchor→current
        // range (standard file-manager semantics); the anchor stays put so a
        // subsequent shift-click re-ranges from the same origin.
        setSelection(rangeIds);
    };

    // Select-all operates on the files currently on screen (i.e. the active
    // search/filter result), which is what "all" means to the user looking at
    // the grid — not every file in the workspace.
    const allSelected = files.length > 0 && selection.length === files.length;

    // The selected files themselves, for the actions that operate on them.
    const selectedFiles = useMemo(
        () => files.filter((f) => selection.includes(f.id)),
        [files, selection],
    );

    const handleSelectAll = useCallback(() => {
        setSelection(files.map((f) => f.id));
        setLastSelectedId(null);
    }, [files]);

    const handleClearSelection = useCallback(() => {
        setSelection([]);
        setLastSelectedId(null);
    }, []);

    const handleToggleSelectAll = useCallback(() => {
        if (allSelected) handleClearSelection();
        else handleSelectAll();
    }, [allSelected, handleClearSelection, handleSelectAll]);

    // Ctrl/Cmd+A selects the visible results, Escape clears. Both are ignored
    // while the user is typing (search box, metadata fields) or while a modal
    // owns the screen, so they never steal a native shortcut.
    useEffect(() => {
        const onKeyDown = (e) => {
            const el = e.target;
            const typing =
                el?.isContentEditable ||
                ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName);
            if (typing) return;
            if (document.querySelector('[role="dialog"]')) return;

            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
                if (files.length === 0) return;
                e.preventDefault();
                handleSelectAll();
            } else if (e.key === 'Escape' && selection.length > 0) {
                handleClearSelection();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [files.length, selection.length, handleSelectAll, handleClearSelection]);

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!selection.length || deleting) return;
        const count = selection.length;
        const confirmed = await confirm({
            title: `Delete ${count} file${count === 1 ? '' : 's'}?`,
            message: 'This permanently removes the selected file(s) and all of their versions. This cannot be undone.',
            confirmText: `Delete ${count} file${count === 1 ? '' : 's'}`,
            cancelText: 'Cancel',
            danger: true,
        });
        if (!confirmed) return;

        // BUG-6: track per-file success so partial failures are surfaced instead
        // of silently clearing the selection and assuming everything worked.
        const succeeded = [];
        const failed = [];
        // Deleting forty files is forty round trips; without this the selection
        // bar stayed fully interactive and gave no sign anything was happening.
        setDeleting(true);
        try {
            for (const id of selection) {
                try {
                    // Suppress per-file toasts; we show one summary below instead.
                    await FileService.deleteFile(id, { skipGlobalErrorToast: true });
                    succeeded.push(id);
                } catch (err) {
                    console.error(`Failed to delete file ${id}:`, err);
                    failed.push(id);
                }
            }
        } finally {
            setDeleting(false);
        }

        // Optimistically drop successfully-deleted files from the visible grid
        // so the list isn't stale (the Algolia panel re-syncs on next search).
        if (succeeded.length) {
            setFiles(prev => prev.filter(f => !succeeded.includes(f.id)));
        }
        // Keep any files that failed to delete selected; clear the rest.
        setSelection(failed);

        if (failed.length) {
            reportError(
                `${failed.length} of ${selection.length} file(s) could not be deleted. Please try again.`,
                { title: 'Some files could not be deleted' }
            );
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div
            className="file-browser-container qd-page qd-page--fill"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className="fb-drop-overlay">
                    {/* Two targets, so a drag can mean either thing. The
                        toolbar's "Find matches" button is the discoverable and
                        keyboard-reachable route; this is the fast one for
                        someone already dragging. Missing both panels falls
                        through to the container handler and uploads, which is
                        the safe default and what a drop did before this. */}
                    <div className="fb-drop-split">
                        <div
                            className={`fb-drop-target ${hoveredTarget === 'upload' ? 'is-over' : ''}`.trim()}
                            onDragEnter={() => setHoveredTarget('upload')}
                            onDragOver={(e) => { e.preventDefault(); setHoveredTarget('upload'); }}
                            onDrop={handleDrop}
                        >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <h3>Add to library</h3>
                            <p>Drop files to upload, or a folder to import</p>
                        </div>

                        <div
                            className={`fb-drop-target ${hoveredTarget === 'search' ? 'is-over' : ''}`.trim()}
                            onDragEnter={() => setHoveredTarget('search')}
                            onDragOver={(e) => { e.preventDefault(); setHoveredTarget('search'); }}
                            onDrop={handleSearchDrop}
                        >
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7"/>
                                <line x1="16.5" y1="16.5" x2="21" y2="21"/>
                            </svg>
                            <h3>Find matches</h3>
                            <p>Search your library by content — crops and clips included. Nothing is uploaded.</p>
                        </div>
                    </div>
                </div>
            )}

            <FileBrowserToolbar
                onFilesChanged={handleFilesChanged}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                visibleColumns={visibleColumns}
                onToggleColumn={(col) => setVisibleColumns(prev =>
                    prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
                )}
                onSearchByFile={setSearchFile}
                sort={sort}
                appliedSort={searchStats.appliedSort}
                onSortChange={setSort}
                density={gridDensity}
                onDensityChange={setGridDensity}
                filtersCollapsed={filtersCollapsed}
                onToggleFilters={() => setFiltersCollapsed((v) => !v)}
            />

            {/* ── Main area: sidebar + results ─────────────────────────── */}
            <div className="file-browser-main">
                {/* Left: the filter rail — resizable, and dismissible entirely */}
                <FilterRail width={railWidth} collapsed={filtersCollapsed} onWidthChange={setRailWidth}>
                    <AlgoliaSearchPanel
                        onResultsChanged={handleAlgoliaResults}
                        onStatsChanged={handleAlgoliaStats}
                        onRegisterRefetch={registerAlgoliaRefetch}
                        initialProjectId={initialProjectId}
                        sort={sort}
                        query={query}
                        onQueryChange={setQuery}
                    />
                </FilterRail>

                {/* Right: the selection bar, pinned above the scrolling results */}
                <div className="file-browser-results">
                <SelectionBar
                    selectedFiles={selectedFiles}
                    resultCount={files.length}
                    allSelected={allSelected}
                    busy={deleting}
                    onSelectAll={handleSelectAll}
                    onClearSelection={handleClearSelection}
                    onDelete={handleDelete}
                    onFilesChanged={handleFilesChanged}
                />
                <div className="file-browser-content">
                    {searchStats.loading ? (
                        <div className="file-grid-view" style={gridVars} aria-busy="true" aria-label="Loading files">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className="file-card file-card--skeleton">
                                    <Skeleton height="var(--qd-card-thumb-h)" radius="0" />
                                    <div className="file-skeleton-info">
                                        <Skeleton text width="70%" />
                                        <Skeleton text width="40%" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : files.length === 0 ? (
                        <EmptyState
                            icon={(
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                                     strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                    <line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" />
                                </svg>
                            )}
                            title="No files to show"
                            description="Drag and drop files anywhere to upload, or use the toolbar to add or import assets. If you've applied filters, try clearing them."
                        />
                    ) : viewMode === 'grid' ? (
                        <FileGridView
                            files={files}
                            density={gridDensity}
                            selection={selection}
                            onFileClick={handleFileClick}
                            onCheckboxClick={handleCheckboxClick}
                            onContextSelect={handleContextSelect}
                        />
                    ) : (
                        <FileListView
                            files={files}
                            selection={selection}
                            visibleColumns={visibleColumns}
                            onFileClick={handleFileClick}
                            onCheckboxClick={handleCheckboxClick}
                            allSelected={allSelected}
                            onToggleSelectAll={handleToggleSelectAll}
                        />
                    )}
                </div>
                </div>
            </div>

            {selectedFileForModal && (
                <FileDetailsModal
                    file={selectedFileForModal}
                    // The results the modal was opened from, so it can step
                    // through them instead of closing and reopening per file.
                    siblings={files}
                    onNavigate={setSelectedFileForModal}
                    onClose={() => setSelectedFileForModal(null)}
                    onFilesChanged={handleFilesChanged}
                />
            )}

            {droppedFiles && createPortal(
                <UploadFileModal
                    initialFiles={droppedFiles}
                    onClose={() => setDroppedFiles(null)}
                    onFilesChanged={handleFilesChanged}
                />,
                document.body
            )}

            {searchFile && createPortal(
                <ReverseSearchModal
                    file={searchFile}
                    onClose={() => setSearchFile(null)}
                    onOpenFile={(id) => {
                        // Only files in the CURRENT result set can be opened
                        // straight into the details modal; anything else would
                        // need a fetch this component has no business doing.
                        const match = files.find((f) => f.id === id);
                        setSearchFile(null);
                        if (match) setSelectedFileForModal(match);
                    }}
                />,
                document.body
            )}

            {importEntries && createPortal(
                <ImportWizardModal
                    initialEntries={importEntries}
                    onClose={() => setImportEntries(null)}
                    onFilesChanged={handleFilesChanged}
                />,
                document.body
            )}
        </div>
    );
};

export default FileBrowser;
